import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useLiveDataQuery } from '@ps/web-shared-generated-graphql/sports';
import type { LiveDataQueryVariables } from '@ps/web-shared-generated-graphql/sports';
import React, { useContext, useReducer } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import { EventTypeId } from '../types/eventType';

import type { ComponentEventTypeEvents, ScoreboardDataReducerAction } from './types';
import { mapEventTypesEventIdsMap, scoreboardsDataReducer } from './utils';

export type LiveDataStateVariables = LiveDataState['variables'];

export interface LiveDataState {
  componentEventTypeEvents: ComponentEventTypeEvents;
  footballPlayerIds: string[];
  variables: Omit<
    LiveDataQueryVariables,
    | 'americanFootballEventIds'
    | 'baseballEventIds'
    | 'basketballEventIds'
    | 'dartsEventIds'
    | 'eSportsEventIds'
    | 'footballEventIds'
    | 'footballPlayerIds'
    | 'handballEventIds'
    | 'iceHockeyEventIds'
    | 'tableTennisEventIds'
    | 'snookerEventIds'
    | 'tennisEventIds'
    | 'volleyballEventIds'
  >;
}

export interface LiveDataContextValue {
  dispatch: React.Dispatch<ScoreboardDataReducerAction>;
  isLoading?: boolean;
  isError?: boolean;
  variables: LiveDataStateVariables;
}

interface Props extends React.PropsWithChildren {
  initialState?: LiveDataState;
  variables?: LiveDataStateVariables;
}

const LiveDataContext = React.createContext<LiveDataContextValue | null>(null);

export const LiveDataProvider: React.FC<Props> = ({
  initialState = {
    componentEventTypeEvents: {},
    footballPlayerIds: [],
    variables: {
      includeTeamStats: false,
      includePlayers: false,
      includeIncidents: false
    }
  },
  children
}) => {
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPEnableLiveData);
  const isEnhancedBetTrackingEnabled = useFeature(FeatureFlag.ISPEnableEnhancedBetTracking);
  const [liveDataState, dispatch] = useReducer(scoreboardsDataReducer, initialState);
  const { componentEventTypeEvents, footballPlayerIds, variables } = liveDataState;
  const isLiveAmericanFootballDataEnabled = useFeature(
    FeatureFlag.ISPEnableLiveAmericanFootballData
  );
  const isLiveBaseballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveBaseballData);
  const isLiveBasketballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveBasketballData);
  const isLiveDartsDataEnabled = useFeature(FeatureFlag.ISPEnableLiveDartsData);
  const isLiveESportsDataEnabled = useFeature(FeatureFlag.ISPEnableLiveESportsData);
  const isLiveFootballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveFootballData);
  const isLiveHandballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveHandballData);
  const isLiveIceHockeyDataEnabled = useFeature(FeatureFlag.ISPEnableLiveIceHockeyData);
  const isLiveSnookerDataEnabled = useFeature(FeatureFlag.ISPEnableLiveSnookerData);
  const isLiveTableTennisDataEnabled = useFeature(FeatureFlag.ISPEnableLiveTableTennisData);
  const isLiveTennisDataEnabled = useFeature(FeatureFlag.ISPEnableLiveTennisData);
  const isLiveVolleyballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveVolleyballData);
  const isDocumentVisible = useSelector(select.documentVisibility.isDocumentVisible);

  const eventTypesEventIdsMap = mapEventTypesEventIdsMap(componentEventTypeEvents);
  const americanFootballEventIds = isLiveAmericanFootballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.AMERICAN_FOOTBALL] ?? [])
    : [];
  const baseballEventIds = isLiveBaseballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.BASEBALL] ?? [])
    : [];
  const basketballEventIds = isLiveBasketballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.BASKETBALL] ?? [])
    : [];
  const dartsEventIds = isLiveDartsDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.DARTS] ?? [])
    : [];
  const eSportsEventIds = isLiveESportsDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.E_SPORTS] ?? [])
    : [];
  const footballEventIds = isLiveFootballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.SOCCER] ?? [])
    : [];
  const handballEventIds = isLiveHandballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.HANDBALL] ?? [])
    : [];
  const iceHockeyEventIds = isLiveIceHockeyDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.ICE_HOCKEY] ?? [])
    : [];
  const snookerEventIds = isLiveSnookerDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.SNOOKER] ?? [])
    : [];
  const tableTennisEventIds = isLiveTableTennisDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.TABLE_TENNIS] ?? [])
    : [];
  const tennisEventIds = isLiveTennisDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.TENNIS] ?? [])
    : [];
  const volleyballEventIds = isLiveVolleyballDataEnabled
    ? (eventTypesEventIdsMap[EventTypeId.VOLLEYBALL] ?? [])
    : [];

  const skipPollAttempt = Boolean(
    !isDocumentVisible ||
      !(isScoreboardsEnabled || isEnhancedBetTrackingEnabled) ||
      (!americanFootballEventIds.length &&
        !baseballEventIds.length &&
        !basketballEventIds.length &&
        !dartsEventIds.length &&
        !eSportsEventIds.length &&
        !footballEventIds.length &&
        !handballEventIds.length &&
        !iceHockeyEventIds.length &&
        !snookerEventIds.length &&
        !tableTennisEventIds.length &&
        !tennisEventIds.length &&
        !volleyballEventIds.length)
  );

  const { loading, error } = useLiveDataQuery({
    variables: {
      americanFootballEventIds: americanFootballEventIds.map(String),
      baseballEventIds: baseballEventIds.map(String),
      basketballEventIds: basketballEventIds.map(String),
      dartsEventIds: dartsEventIds.map(String),
      eSportsEventIds: eSportsEventIds.map(String),
      footballEventIds: footballEventIds.map(String),
      handballEventIds: handballEventIds.map(String),
      iceHockeyEventIds: iceHockeyEventIds.map(String),
      snookerEventIds: snookerEventIds.map(String),
      tableTennisEventIds: tableTennisEventIds.map(String),
      tennisEventIds: tennisEventIds.map(String),
      volleyballEventIds: volleyballEventIds.map(String),
      footballPlayerIds,
      ...variables
    },
    // "skip" is used for the initial render
    skip: skipPollAttempt,
    // "skipPollAttempt" is used for every request after the initial render
    skipPollAttempt: () => skipPollAttempt,
    pollInterval: 5000,
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  return (
    <LiveDataContext.Provider
      value={{
        dispatch,
        isLoading: loading,
        isError: Boolean(error),
        variables
      }}
    >
      {children}
    </LiveDataContext.Provider>
  );
};

export const useLiveData = (): LiveDataContextValue => {
  const context = useContext(LiveDataContext);

  if (context === null) {
    throw new Error('You must wrap "useLiveData" in a "LiveDataProvider"');
  }

  return context;
};
